import { useReducer } from "react";
import type { IntegrationStatus, OnboardingStatus } from "../types";

interface WalletDetailState {
	modals: {
		addTransaction: boolean;
		fee: boolean;
		delete: boolean;
		whitelist: boolean;
		productionInfo: boolean;
		productionEdit: boolean;
		moreInfo: boolean;
	};
	forms: {
		comment: string;
		transactionType: "Deposit" | "Withdraw";
		transactionRef: string;
		agentId: string;
		feeStart: string;
		feeEnd: string;
		feeAmount: string;
		whitelistEnvs: string[];
		slackChannel: string;
	};
	ui: {
		activeWorkstream: "integration" | "onboarding";
		proposalActionLoading: boolean;
		viewingIntegrationStep?: IntegrationStatus;
		viewingOnboardingStep?: OnboardingStatus;
	};
}

type Action =
	| {
			type: "SET_MODAL";
			modal: keyof WalletDetailState["modals"];
			open: boolean;
	  }
	| { type: "TOGGLE_MODAL"; modal: keyof WalletDetailState["modals"] }
	| {
			type: "SET_FORM_FIELD";
			field: keyof WalletDetailState["forms"];
			value: string | string[] | "Deposit" | "Withdraw";
	  }
	| { type: "SET_ACTIVE_WORKSTREAM"; value: "integration" | "onboarding" }
	| { type: "SET_PROPOSAL_LOADING"; value: boolean }
	| { type: "RESET_TRANSACTION_FORM" }
	| { type: "RESET_FEE_FORM" }
	| { type: "RESET_WHITELIST_FORM" }
	| { type: "TOGGLE_WHITELIST_ENV"; env: string }
	| { type: "SYNC_WALLET_INPUTS"; agentId: string; slackChannel: string }
	| { type: "SET_VIEWING_INTEGRATION_STEP"; step: IntegrationStatus }
	| { type: "SET_VIEWING_ONBOARDING_STEP"; step: OnboardingStatus };

const initialState: WalletDetailState = {
	modals: {
		addTransaction: false,
		fee: false,
		delete: false,
		whitelist: false,
		productionInfo: false,
		productionEdit: false,
		moreInfo: false,
	},
	forms: {
		comment: "",
		transactionType: "Deposit",
		transactionRef: "",
		agentId: "",
		feeStart: "",
		feeEnd: "",
		feeAmount: "",
		whitelistEnvs: [],
		slackChannel: "",
	},
	ui: {
		activeWorkstream: "integration",
		proposalActionLoading: false,
		viewingIntegrationStep: undefined,
		viewingOnboardingStep: undefined,
	},
};

function reducer(state: WalletDetailState, action: Action): WalletDetailState {
	switch (action.type) {
		case "SET_MODAL":
			return {
				...state,
				modals: { ...state.modals, [action.modal]: action.open },
			};
		case "TOGGLE_MODAL":
			return {
				...state,
				modals: {
					...state.modals,
					[action.modal]: !state.modals[action.modal],
				},
			};
		case "SET_FORM_FIELD":
			return {
				...state,
				forms: { ...state.forms, [action.field]: action.value },
			};
		case "SET_ACTIVE_WORKSTREAM":
			return {
				...state,
				ui: { ...state.ui, activeWorkstream: action.value },
			};
		case "SET_PROPOSAL_LOADING":
			return {
				...state,
				ui: { ...state.ui, proposalActionLoading: action.value },
			};
		case "RESET_TRANSACTION_FORM":
			return {
				...state,
				forms: { ...state.forms, transactionRef: "" },
				modals: { ...state.modals, addTransaction: false },
			};
		case "RESET_FEE_FORM":
			return {
				...state,
				forms: { ...state.forms, feeStart: "", feeEnd: "", feeAmount: "" },
				modals: { ...state.modals, fee: false },
			};
		case "RESET_WHITELIST_FORM":
			return {
				...state,
				forms: { ...state.forms, whitelistEnvs: [] },
				modals: { ...state.modals, whitelist: false },
			};
		case "TOGGLE_WHITELIST_ENV": {
			const envs = state.forms.whitelistEnvs;
			const newEnvs = envs.includes(action.env)
				? envs.filter((e) => e !== action.env)
				: [...envs, action.env];
			return {
				...state,
				forms: { ...state.forms, whitelistEnvs: newEnvs },
			};
		}
		case "SYNC_WALLET_INPUTS":
			return {
				...state,
				forms: {
					...state.forms,
					agentId: action.agentId,
					slackChannel: action.slackChannel,
				},
			};
		case "SET_VIEWING_INTEGRATION_STEP":
			return {
				...state,
				ui: { ...state.ui, viewingIntegrationStep: action.step },
			};
		case "SET_VIEWING_ONBOARDING_STEP":
			return {
				...state,
				ui: { ...state.ui, viewingOnboardingStep: action.step },
			};
		default:
			return state;
	}
}

export function useWalletDetailState() {
	return useReducer(reducer, initialState);
}
